/**
 * Course-page restricted mode experiment (`course_page_restricted_mode`).
 *
 * Target stage: a student on a monthly tutoring subscription whose renewal
 * invoice has failed at least twice (the backend records this on
 * `CourseStudent.subscriptionPayment`). Today the course page shows nothing
 * about it — the family keeps every class, recording and chat until Stripe
 * cancels the plan, and churns without ever being asked to pay.
 *
 * This is a painted-door test, not the real feature. Nothing is enforced
 * server-side. The test variant sees a "restricted mode" banner, and the
 * Book-a-Lesson button and the tutor chat composer are replaced by a prompt
 * with one CTA — "I'll renew account" — and a close button. Each response is
 * posted to the experiment's Google Chat space (and to PostHog) so the team
 * can measure intent and follow up by hand before building the real locks.
 *
 * Variants (PostHog multivariate flag):
 * - control: the page exactly as it was.
 * - test:    banner + prompt.
 *
 * Goal metric: `course_page_restricted_mode_responded` with `action: RENEW`
 * (fired by the backend when the response is recorded). Recovery is tracked
 * by `course_page_restricted_mode_converted_from_backend` when the invoice is
 * paid. Secondary: `_shown`, `_prompt_opened`. Every event carries `variant`.
 */
export const COURSE_PAGE_RESTRICTED_MODE_FEATURE_FLAG =
  'course_page_restricted_mode';

export const COURSE_PAGE_RESTRICTED_MODE_VARIANTS = {
  CONTROL: 'control',
  TEST: 'test',
} as const;

/** Mirrors `ECourseStudentSubscriptionPaymentState` on the backend. */
export const COURSE_SUBSCRIPTION_PAYMENT_STATES = {
  /** First failed renewal attempt — the experiment does not show yet. */
  PAST_DUE: 'PAST_DUE',
  /** Failed at least twice — the experiment shows. */
  RESTRICTED: 'RESTRICTED',
} as const;

export type TCourseSubscriptionPaymentState = typeof COURSE_SUBSCRIPTION_PAYMENT_STATES[keyof typeof COURSE_SUBSCRIPTION_PAYMENT_STATES];

/** Where the prompt was opened from. Mirrors `ERestrictedModeExperimentSource`. */
export const RESTRICTED_MODE_PROMPT_SOURCES = {
  BANNER: 'BANNER',
  BOOK_LESSON: 'BOOK_LESSON',
  CHAT: 'CHAT',
} as const;

export type TRestrictedModePromptSource = typeof RESTRICTED_MODE_PROMPT_SOURCES[keyof typeof RESTRICTED_MODE_PROMPT_SOURCES];

/** What the student did with the prompt. Mirrors `ERestrictedModeExperimentAction`. */
export const RESTRICTED_MODE_RESPONSE_ACTIONS = {
  RENEW: 'RENEW',
  WONT_RENEW: 'WONT_RENEW',
  CLOSE: 'CLOSE',
} as const;

export type TRestrictedModeResponseAction = typeof RESTRICTED_MODE_RESPONSE_ACTIONS[keyof typeof RESTRICTED_MODE_RESPONSE_ACTIONS];
